import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const FeatureContext = createContext();

const API = process.env.REACT_APP_BACKEND_URL || '';

export const FeatureProvider = ({ children }) => {
  const { token } = useAuth();
  const [freePlatform, setFreePlatform] = useState(false);
  const [features, setFeatures] = useState({
    all_features: true,
    enabled_features: [],
    plan_id: 'trial',
    loading: true
  });
  const [limits, setLimits] = useState({
    max_hives: 20,
    max_crew: 1,
    max_inspections: 200,
    current_hives: 0,
    current_crew: 0,
    current_inspections: 0
  });

  // Fetch free_platform flag from public settings (once)
  useEffect(() => {
    fetch(`${API}/api/app-settings/public`).then(r => r.json())
      .then(d => { if (d?.free_platform) setFreePlatform(true); })
      .catch(() => {});
  }, []);

  const fetchFeatures = useCallback(async () => {
    if (!token) {
      setFeatures({
        all_features: true,
        enabled_features: [],
        plan_id: 'trial',
        loading: false
      });
      return;
    }

    try {
      const response = await fetch(`${API}/api/subscription/features`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFeatures({
          all_features: data.all_features,
          enabled_features: data.enabled_features || [],
          plan_id: data.plan_id,
          loading: false
        });
      } else {
        setFeatures(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      setFeatures(prev => ({ ...prev, loading: false }));
    }
  }, [token]);

  const fetchLimits = useCallback(async () => {
    if (!token) return;

    try {
      const [hiveRes, crewRes, inspRes] = await Promise.all([
        fetch(`${API}/api/subscription/can-add-hive`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/subscription/can-add-crew`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/subscription/can-add-inspection`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const [hiveData, crewData, inspData] = await Promise.all([
        hiveRes.ok ? hiveRes.json() : {},
        crewRes.ok ? crewRes.json() : {},
        inspRes.ok ? inspRes.json() : {}
      ]);

      setLimits({
        max_hives: hiveData.max_hives || 20,
        max_crew: crewData.max_crew || 1,
        max_inspections: inspData.max_inspections || 200,
        current_hives: hiveData.current_count || 0,
        current_crew: crewData.current_count || 0,
        current_inspections: inspData.current_count || 0,
        can_add_hive: hiveData.can_add !== false,
        can_add_crew: crewData.can_add !== false,
        can_add_inspection: inspData.can_add !== false
      });
    } catch (error) {
    }
  }, [token]);

  useEffect(() => {
    fetchFeatures();
    fetchLimits();
  }, [fetchFeatures, fetchLimits]);

  // Check if a specific feature is enabled for the user's plan
  const hasFeature = useCallback((featureId) => {
    if (features.loading) return true; // Allow access while loading
    if (features.all_features) return true;
    return features.enabled_features.includes(featureId);
  }, [features]);

  // Feature IDs:
  // - queen_rearing
  // - analytics
  // - hive_costs
  // - marketplace
  // - knowledge_library
  // - hive_mind
  // - swarm_traps
  // - reports_export

  const refreshFeatures = useCallback(() => {
    fetchFeatures();
    fetchLimits();
  }, [fetchFeatures, fetchLimits]);

  return (
    <FeatureContext.Provider value={{
      features,
      limits,
      hasFeature,
      refreshFeatures,
      freePlatform,
      loading: features.loading
    }}>
      {children}
    </FeatureContext.Provider>
  );
};

export const useFeatures = () => {
  const context = useContext(FeatureContext);
  if (!context) {
    throw new Error('useFeatures must be used within a FeatureProvider');
  }
  return context;
};

export default FeatureContext;
